/**
 * @file 主体资产数据类型定义
 * @author sulu@baidu.com
 */

import type {OnlyProjectIdParams, ProjectId} from '@/types';
import type {MediaTypeValue} from '@/types/media';

/** ****** 主体资产基础公共类型定义 ****** */

/** 资产类型（合并 AssetType 与 AssetSubType，按数字值合并） */
export const AssetTypeValue = {
    /** 角色 */
    CHARACTER: 1,
    /** 场景 */
    SCENE: 2,
    /** 道具 */
    PROP: 3,
    /** 角色装扮 */
    CHARACTER_OUTFIT: 11,
    /** 所有 */
    ALL: -1,
} as const;

/** 父类型：CHARACTER | SCENE | PROP */
export type AssetType = (typeof AssetTypeValue)['CHARACTER' | 'SCENE' | 'PROP'];

/** 子类型：全集（含 CHARACTER_OUTFIT、ALL） */
export type AssetSubType = (typeof AssetTypeValue)[keyof typeof AssetTypeValue];

/** 图组审核/候选状态 */
export const CandidateStatusValue = {
    /** 默认 */
    DEFAULT: 0,
    /** 通过 */
    PRIMARY: 1,
    /** 待定 */
    CANDIDATE: 2,
    /** 淘汰 */
    ELIMINATED: 3,
} as const;

export type CandidateStatus = (typeof CandidateStatusValue)[keyof typeof CandidateStatusValue];

/** 审核状态优先级：通过 > 待定 > 待审核 > 淘汰，既是图组展示顺序，也是侧栏标签的取值顺序 */
export const ASSET_STATUS_PRIORITY = [
    CandidateStatusValue.PRIMARY,
    CandidateStatusValue.CANDIDATE,
    CandidateStatusValue.DEFAULT,
    CandidateStatusValue.ELIMINATED,
] as const;

/** 单张位图信息 */
export interface AssetImage {
    /** 数据唯一ID */
    id: number;
    /** 生成记录ID */
    genId: string;
    /** 图片访问URL */
    imageUrl: string;
    /** 前端缩略图URL（由 imageUrl 压缩转化，预览/审核弹窗等原图场景请用 imageUrl） */
    thumbnailImageUrl?: string;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    labelName?: string;
}

/** 位图组信息 (ImageSet) */
export interface AssetImageSet {
    /** 图组ID */
    setId: number;
    /** 图组编码 */
    setCode?: string;
    /** 图组名称 */
    setName?: string;
    /** 创建人/上传者名称 */
    creator: string;
    /** 当前用户是否为该图组的创建者 */
    isOwner?: boolean;
    /** 组内图片列表 */
    setImages: AssetImage[];
    /** 组候选状态：0-默认, 1-首选, 2-待定/候补, 3-淘汰 */
    candidateStatus: CandidateStatus;
    /** 审核备注 */
    remark?: string;
}

/** 角色装扮信息 */
export interface CharacterImageSetsInfo {
    /** 装扮ID */
    id: number;
    /** 装扮名称（如：战损装） */
    name: string;
    /** 装扮描述（prompt 提示词） */
    description?: string;
    /** 该项下的图组列表 */
    imageSets: AssetImageSet[];
    /** 装扮出场集数（needEpisodes=true 时返回） */
    episodes?: number[];
}

/** ****** 主体资产联合类型定义 ****** */

/** 资产基础元数据 */
interface BaseAsset {
    /** 资产ID */
    readonly id: number;
    /** 资产名称 (角色名/场景名/道具名) */
    name: string;
    /** 资产排序序号 */
    sortOrder: number;
}

/** 1 - 角色资产定义 */
export interface CharacterAsset extends BaseAsset {
    /** 角色小传 */
    biography?: string;
    /** 音色ID */
    voiceId?: string;
    /** 音色名称 */
    voiceName?: string;
    /** 音色模型名称 */
    voiceModelName?: string;
    /** 音色试听/音调 URL */
    voiceToneUrl?: string;
    /** ttsId */
    ttsId?: string;
    /** 语速 */
    voiceSpeed?: string;
    /** 角色装扮信息列表 */
    outfitInfo: CharacterImageSetsInfo[];
    /** 一级卡出场集数（该角色所有装扮并集，后端动态计算；needEpisodes=true 时返回） */
    episodes?: number[];
}

/** 场景与道具的共有结构 */
export interface GenericAsset extends BaseAsset {
    /** 提示词描述 */
    description?: string;
    /** 图集列表 */
    imageSets?: AssetImageSet[];
    /** 场景 / 道具出场集数（needEpisodes=true 时返回） */
    episodes?: number[];
}

/**
 * 场景（type=2）/ 道具（type=3）资产定义。
 * 二者结构与 GenericAsset 完全一致，仅作语义别名以在调用处标注资产类型，不额外扩展字段。
 */
export type SceneAsset = GenericAsset;
export type PropAsset = GenericAsset;

/** 资产联合类型 */
export type Asset = CharacterAsset | GenericAsset;

/** ****** 接口请求/响应类型定义 ****** */

/** 资产列表请求参数 */
export interface AssetListParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：1-角色 2-场景 3-道具 */
    type: AssetType;
    /** 是否要setImages填充空位 */
    needFill?: boolean;
    /** 是否返回出场集数（资产页传 true） */
    needEpisodes?: boolean;
    /** 出场集筛选集号：指定集号仅返回这些集出场的资产；不传/空数组表示全剧（接口预留多选，UI 暂仅单选） */
    episodeNos?: number[];
}

/** 资产列表响应 data */
export interface AssetListData {
    /** 总数 */
    total: number;
    /** 资产列表 */
    list: Asset[];
    /** 提取状态（提取中时返回，用于轮询判断） */
    extractStatus?: ExtractStatus;
    /** 当前提取任务 ID */
    extractTaskId?: string;
    /** 提取成功已读状态：0-未读 1-已读 */
    readStatus?: 0 | 1;
}

/**
 * 资产单图删除类型枚举
 * 两组type区分不同数据维度
 *  * AssetImageTypeValue是删除资产图片
 *  * AssetTypeValue 是删除资产组
 */
export const AssetImageTypeValue = {
    /** 角色装扮图片 */
    CHARACTER_OUTFIT: 1100,
    /** 场景图片 */
    SCENE: 200,
    /** 道具图片 */
    PROP: 300,
} as const;

export type AssetImageType = (typeof AssetImageTypeValue)[keyof typeof AssetImageTypeValue];

/**
 * 资产创建请求参数
 */
export interface AssetCreateParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：1-角色 11-角色装扮 2-场景 3-道具 -1-所有 */
    type: AssetSubType;
    /** 角色ID（角色装扮时必传） */
    characterId?: number;
    /** 前一个序号 */
    preSortOrder?: number;
    /** 后一个序号 */
    nextSortOrder?: number;
}

/**
 * 资产删除请求参数
 */
export interface AssetDeleteParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：1-角色 11-角色装扮 2-场景 3-道具 */
    type: AssetSubType;
    /** 资产ID */
    id: number;
}

/**
 * 资产单图删除请求参数
 */
export interface AssetImageDeleteParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 单图类型：1110-角色装扮 200-场景 300-道具 */
    type: AssetImageType;
    /** 图片ID */
    id: number;
}

/** 提取状态枚举 */
export const ExtractStatusValue = {
    /** 失败 */
    FAILED: -1,
    /** 初始态 */
    INITIAL: 0,
    /** 提取中（全量/增量统一） */
    EXTRACTING: 1,
    /** 提取成功 */
    SUCCESS: 2,
} as const;

export type ExtractStatus = (typeof ExtractStatusValue)[keyof typeof ExtractStatusValue];

/** 资产解析请求参数 */
export type AssetExtractParams = OnlyProjectIdParams;

/** 资产解析响应 data */
export interface AssetExtractData {
    /** 提取状态：-1失败 0初始态 1提取中 2提取成功 */
    status: ExtractStatus;
    /** 解析任务ID */
    taskId: string;
    /** 解析信息（失败原因等） */
    taskResultMsg?: string;
}

/**
 * 资产解析任务轮询请求参数
 */
export interface AssetExtractPollingParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 任务ID */
    taskId: string;
}

/** 解析出的装扮/场景/道具信息 */
export interface ExtractAssetItem {
    /** 名称 */
    name: string;
    /** 描述 */
    description: string;
}

/** 解析出的角色信息 */
export interface ExtractCharacter {
    /** 角色名称 */
    name: string;
    /** 角色小传 */
    biography: string;
    /** 是否角色已存在 */
    isExisting?: boolean;
    /** 存在角色的ID */
    characterId?: number;
    /** 角色装扮 */
    outfitInfo?: ExtractAssetItem[];
}

/** 解析结果（增量解析成功时返回） */
export interface ExtractAssets {
    /** 角色列表 */
    characters: ExtractCharacter[];
    /** 场景列表 */
    scenes: ExtractAssetItem[];
    /** 道具列表 */
    props: ExtractAssetItem[];
}

/** 资产解析任务轮询响应 data */
export interface AssetExtractPollingData {
    /** 解析状态：-1失败 0初始态 1初始提取中 2增量提取中 3提取成功 */
    status: ExtractStatus;
    /** 解析结果（增量解析成功时返回） */
    extractAssets?: ExtractAssets;
    /** 解析信息（失败原因等） */
    taskResultMsg?: string;
}

/**
 * 资产解析内容确认新增请求参数
 */
export interface AssetExtractAddParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 解析资产结果 */
    extractAssets: ExtractAssets;
}

/**
 * 性别选项
 */
export interface GenderOption {
    label: string;
    value: number;
}

/**
 * 音色年龄段选项
 */
export interface VoiceAgeOption {
    label: string;
    value: number;
}

/**
 * 音色音速选项
 */
export interface VoiceSpeedOption {
    label: string;
    value: string;
}

/**
 * 音色音速过滤配置
 */
export interface VoiceFilterConf {
    /** 音色年龄段配置 */
    ageConf: VoiceAgeOption[];
    /** 语速配置 */
    voiceSpeedConf: VoiceSpeedOption[];
}

/**
 * 资产页初始化响应 data
 */
export interface AssetInitData {
    /** 是否有资产 */
    hasAsset: boolean;
    /** 是否有确认剧本 */
    hasConfirmedScript: boolean;
    /** 剧本是否有更新 */
    scriptChange: boolean;
    /** 是否提取解析过（有首次解析记录即为true） */
    hasExtracted: boolean;
    /** 提取状态：-1失败 0初始态 1提取中 2提取成功 */
    extractStatus: ExtractStatus;
    /** 提取任务ID */
    extractTaskId: string;
    /** 解析信息（失败原因等） */
    taskResultMsg?: string;
    /** 音色音速过滤配置 */
    voiceFilterConf: VoiceFilterConf;
}

/**
 * 资产名称更新请求参数
 */
export interface AssetNameUpdateParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：1-角色 11-角色装扮 2-场景 3-道具 */
    type: AssetSubType;
    /** 资产ID */
    id: number;
    /** 名称 */
    name: string;
}

/**
 * 角色小传更新请求参数
 */
export interface CharacterBiographyUpdateParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 角色ID */
    characterId: number;
    /** 角色小传描述 */
    biography: string;
}

/**
 * 角色音色更新请求参数
 */
export interface CharacterVoiceUpdateParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 角色ID */
    characterId: number;
    /** 音色ID */
    voiceName: string;
    /** 音速 */
    voiceSpeed: string;
}

/**
 * 资产提示词更新请求参数
 */
export interface AssetPromptUpdateParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：11-角色装扮 2-场景 3-道具 */
    type: AssetSubType;
    /** 资产ID */
    id: number;
    /** 提示词 */
    prompt: string;
}

/**
 * 剧集列表请求参数（「修改出场集」弹窗数据源）
 */
export interface AssetEpisodeListParams {
    /** 项目ID */
    projectId: ProjectId;
}

/** 单个剧集 */
export interface EpisodeItem {
    /** 剧集编号 */
    episodeNo: number;
    /** 剧集标题 */
    title: string;
}

/** 剧集列表 */
export interface AssetEpisodeListData {
    /** 已提取资产对应剧本版本下的全部剧集 */
    episodes: EpisodeItem[];
}

/**
 * 出场集更新请求参数
 */
export interface AssetEpisodesUpdateParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：11-角色装扮 2-场景 3-道具 */
    type: AssetSubType;
    /** 资产ID（装扮ID / 场景ID / 道具ID） */
    id: number;
    /** 完整的出场集数组 */
    episodes: number[];
}

/**
 * 资产审核状态更新请求参数
 */
export interface AssetSetAuditParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 资产类型：11-角色装扮 2-场景 3-道具 */
    type: AssetSubType;
    /** 图组ID */
    setId: number;
    /** 备选状态：1-首选 2-备选 3-淘汰 */
    status: CandidateStatus;
    /** 审核备注 (淘汰时必填) */
    remark?: string;
}

/**
 * 音色列表请求参数
 */
export interface VoiceListParams {
    /** 性别：1-男性 2-女性 */
    gender?: number;
    /** 年龄阶段（枚举值待定）：1-中年 2-老年 3-少年 */
    age?: number;
}

/** 音色信息 */
export interface VoiceItem {
    /** 音色名称 */
    name: string;
    /** 音色ID */
    id: string;
    /** 模型名称 */
    modelName: string;
    /** 性别 */
    gender: number;
    /** 年龄 */
    age: number;
    /** 封面 */
    coverUrl: string;
    /** 压缩的封面图 */
    thumbnailCoverUrl?: string;
    /** 音色URL */
    toneUrl: string;
    /** 音色描述 */
    description: string;
    /** ttsId */
    ttsId?: string;
}

/** 资产引用检索请求参数 */
export interface AssetMentionListParams {
    /** 项目ID */
    projectId: ProjectId;
    /** 分集ID，服务端据此判断 inEpisode 并优先排序 */
    episodeId?: number;
}

/**
 * 资产下的单张可选图（二级项）
 *
 * 字段与富文本协议的 ResourceItem 对齐，可直接作为引用节点数据插入
 */
export interface AssetReferenceMediaItem {
    /** 图片唯一ID */
    id: string;
    /** 所属资产ID */
    assetId: string;
    /** 图片展示名，如 叶南鸿_古代锦缎公子常服_正面全身1 */
    displayName: string;
    /** 资产类型，继承父级 */
    assetType: AssetType;
    /** 媒体类型，固定 image */
    mediaType: MediaTypeValue;
    /** 缩略图地址 */
    thumbnailUrl: string;
    /** 原图地址 */
    url: string;
    width?: number;
    height?: number;
    /** 资源状态，取值与富文本 RESOURCE_ITEM_STATUS 一致 */
    status: 'normal' | 'no_resource' | 'deleted';
    /** label 原始值（如 front_full），用于前端分组 */
    label?: string;
    /** label 中文展示名（如 正面全身） */
    labelName?: string;
}

/** 可被 @ 引用的资产（一级项） */
export interface AssetReferenceItem {
    /** 资产唯一ID：角色=装扮ID，场景=场景ID，道具=道具ID */
    id: string;
    /** 资产展示名，如 叶南鸿_古代锦缎公子常服 */
    displayName: string;
    /** 资产类型：1-角色 2-场景 3-道具 */
    assetType: AssetType;
    /** 资产图标/首图缩略图，无则为空字符串 */
    iconUrl: string;
    /** 是否在当前分集中出现，仅作排序提示，顺序由服务端给定 */
    inEpisode: boolean;
    /** 检索关键词（别名等），参与本地模糊匹配 */
    keywords?: string[];
    /** 该资产下可选的图片列表，顺序由服务端决定，前端不重排 */
    mediaList: AssetReferenceMediaItem[];
}

/** 资产引用检索响应 data */
export interface AssetReferenceListData {
    /** 全部资产平铺，服务端按 inEpisode 优先排序 */
    list: AssetReferenceItem[];
}
